/**
 * Global Styles Component
 * Provides global CSS styles and CSS custom properties
 */

import { createGlobalStyle } from 'styled-components';

export const GlobalStyles = createGlobalStyle`
  /* CSS Custom Properties for theme values */
  :root {
    /* Colors - Light theme (default) */
    --color-background-primary: ${({ theme }) => theme.colors.background.primary};
    --color-background-secondary: ${({ theme }) => theme.colors.background.secondary};
    --color-background-tertiary: ${({ theme }) => theme.colors.background.tertiary};
    --color-background-elevated: ${({ theme }) => theme.colors.background.elevated};
    --color-background-overlay: ${({ theme }) => theme.colors.background.overlay};

    --color-text-primary: ${({ theme }) => theme.colors.text.primary};
    --color-text-secondary: ${({ theme }) => theme.colors.text.secondary};
    --color-text-tertiary: ${({ theme }) => theme.colors.text.tertiary};
    --color-text-disabled: ${({ theme }) => theme.colors.text.disabled};
    --color-text-inverse: ${({ theme }) => theme.colors.text.inverse};
    --color-text-link: ${({ theme }) => theme.colors.text.link};
    --color-text-link-hover: ${({ theme }) => theme.colors.text.linkHover};

    --color-border-primary: ${({ theme }) => theme.colors.border.primary};
    --color-border-secondary: ${({ theme }) => theme.colors.border.secondary};
    --color-border-focus: ${({ theme }) => theme.colors.border.focus};
    --color-border-error: ${({ theme }) => theme.colors.border.error};
    --color-border-success: ${({ theme }) => theme.colors.border.success};
    --color-border-warning: ${({ theme }) => theme.colors.border.warning};

    --color-interactive-primary: ${({ theme }) => theme.colors.interactive.primary};
    --color-interactive-primary-hover: ${({ theme }) => theme.colors.interactive.primaryHover};
    --color-interactive-primary-active: ${({ theme }) => theme.colors.interactive.primaryActive};
    --color-interactive-primary-disabled: ${({ theme }) => theme.colors.interactive.primaryDisabled};
    --color-interactive-secondary: ${({ theme }) => theme.colors.interactive.secondary};
    --color-interactive-secondary-hover: ${({ theme }) => theme.colors.interactive.secondaryHover};
    --color-interactive-secondary-active: ${({ theme }) => theme.colors.interactive.secondaryActive};

    --color-status-success: ${({ theme }) => theme.colors.status.success};
    --color-status-success-background: ${({ theme }) => theme.colors.status.successBackground};
    --color-status-warning: ${({ theme }) => theme.colors.status.warning};
    --color-status-warning-background: ${({ theme }) => theme.colors.status.warningBackground};
    --color-status-error: ${({ theme }) => theme.colors.status.error};
    --color-status-error-background: ${({ theme }) => theme.colors.status.errorBackground};
    --color-status-info: ${({ theme }) => theme.colors.status.info};
    --color-status-info-background: ${({ theme }) => theme.colors.status.infoBackground};

    /* Typography */
    --font-family-sans: ${({ theme }) => theme.fontFamilies.sans};
    --font-family-mono: ${({ theme }) => theme.fontFamilies.mono};
    --font-family-serif: ${({ theme }) => theme.fontFamilies.serif};

    /* Spacing */
    --spacing-xs: ${({ theme }) => theme.semanticSpacing.component.xs};
    --spacing-sm: ${({ theme }) => theme.semanticSpacing.component.sm};
    --spacing-md: ${({ theme }) => theme.semanticSpacing.component.md};
    --spacing-lg: ${({ theme }) => theme.semanticSpacing.component.lg};
    --spacing-xl: ${({ theme }) => theme.semanticSpacing.component.xl};
    --spacing-2xl: ${({ theme }) => theme.semanticSpacing.component['2xl']};

    /* Border radius */
    --border-radius-sm: ${({ theme }) => theme.borderRadius.sm};
    --border-radius-base: ${({ theme }) => theme.borderRadius.base};
    --border-radius-md: ${({ theme }) => theme.borderRadius.md};
    --border-radius-lg: ${({ theme }) => theme.borderRadius.lg};
    --border-radius-xl: ${({ theme }) => theme.borderRadius.xl};
    --border-radius-2xl: ${({ theme }) => theme.borderRadius['2xl']};
    --border-radius-full: ${({ theme }) => theme.borderRadius.full};

    /* Shadows */
    --shadow-sm: ${({ theme }) => theme.shadows.sm};
    --shadow-base: ${({ theme }) => theme.shadows.base};
    --shadow-md: ${({ theme }) => theme.shadows.md};
    --shadow-lg: ${({ theme }) => theme.shadows.lg};
    --shadow-xl: ${({ theme }) => theme.shadows.xl};
    --shadow-2xl: ${({ theme }) => theme.shadows['2xl']};
    --shadow-inner: ${({ theme }) => theme.shadows.inner};
    --shadow-outline: ${({ theme }) => theme.shadows.outline};

    /* Breakpoints */
    --breakpoint-sm: ${({ theme }) => theme.breakpoints.sm}px;
    --breakpoint-md: ${({ theme }) => theme.breakpoints.md}px;
    --breakpoint-lg: ${({ theme }) => theme.breakpoints.lg}px;
    --breakpoint-xl: ${({ theme }) => theme.breakpoints.xl}px;
    --breakpoint-2xl: ${({ theme }) => theme.breakpoints['2xl']}px;

    /* Touch targets */
    --touch-target-minimum: ${({ theme }) => theme.touchTargets.minimum};
    --touch-target-comfortable: ${({ theme }) => theme.touchTargets.comfortable};
    --touch-target-large: ${({ theme }) => theme.touchTargets.large};
  }

  /* CSS Reset and Base Styles */
  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }

  * {
    margin: 0;
    padding: 0;
  }

  html {
    /* Improve text rendering */
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
    
    /* Prevent font size inflation on mobile */
    -webkit-text-size-adjust: 100%;
    -moz-text-size-adjust: 100%;
    text-size-adjust: 100%;
    
    /* Enable smooth scrolling */
    scroll-behavior: smooth;
    
    /* Set base font size for rem calculations */
    font-size: 16px;
  }

  body {
    font-family: var(--font-family-sans);
    font-size: ${({ theme }) => theme.fontSizes.base};
    font-weight: ${({ theme }) => theme.fontWeights.normal};
    line-height: ${({ theme }) => theme.lineHeights.normal};
    color: var(--color-text-primary);
    background-color: var(--color-background-primary);
    
    /* Prevent horizontal scrolling */
    overflow-x: hidden;
    
    /* Improve touch scrolling on iOS */
    -webkit-overflow-scrolling: touch;
  }

  /* Typography Elements */
  h1, h2, h3, h4, h5, h6 {
    font-weight: ${({ theme }) => theme.fontWeights.semiBold};
    line-height: ${({ theme }) => theme.lineHeights.tight};
    color: var(--color-text-primary);
    margin-bottom: ${({ theme }) => theme.spacing[4]};
  }

  h1 {
    font-size: ${({ theme }) => theme.typography.heading.h1.fontSize};
    font-weight: ${({ theme }) => theme.typography.heading.h1.fontWeight};
  }

  h2 {
    font-size: ${({ theme }) => theme.typography.heading.h2.fontSize};
    font-weight: ${({ theme }) => theme.typography.heading.h2.fontWeight};
  }

  h3 {
    font-size: ${({ theme }) => theme.typography.heading.h3.fontSize};
    font-weight: ${({ theme }) => theme.typography.heading.h3.fontWeight};
  }

  h4 {
    font-size: ${({ theme }) => theme.typography.heading.h4.fontSize};
    font-weight: ${({ theme }) => theme.typography.heading.h4.fontWeight};
  }

  h5 {
    font-size: ${({ theme }) => theme.typography.heading.h5.fontSize};
    font-weight: ${({ theme }) => theme.typography.heading.h5.fontWeight};
  }

  h6 {
    font-size: ${({ theme }) => theme.typography.heading.h6.fontSize};
    font-weight: ${({ theme }) => theme.typography.heading.h6.fontWeight};
  }

  p {
    margin-bottom: ${({ theme }) => theme.spacing[4]};
    line-height: ${({ theme }) => theme.lineHeights.relaxed};
  }

  a {
    color: var(--color-text-link);
    text-decoration: none;
    transition: color 0.2s ease-in-out;

    &:hover {
      color: var(--color-text-link-hover);
      text-decoration: underline;
    }

    &:focus {
      outline: 2px solid var(--color-border-focus);
      outline-offset: 2px;
      border-radius: ${({ theme }) => theme.borderRadius.sm};
    }
  }

  /* Form Elements */
  button, input, select, textarea {
    font-family: inherit;
    font-size: inherit;
  }

  button {
    cursor: pointer;
    border: none;
    background: none;
    
    &:disabled {
      cursor: not-allowed;
    }
  }

  input, select, textarea {
    &:focus {
      outline: 2px solid var(--color-border-focus);
      outline-offset: 2px;
    }
  }

  /* Lists */
  ul, ol {
    padding-left: ${({ theme }) => theme.spacing[6]};
    margin-bottom: ${({ theme }) => theme.spacing[4]};
  }

  li {
    margin-bottom: ${({ theme }) => theme.spacing[1]};
  }

  /* Code */
  code, pre {
    font-family: var(--font-family-mono);
    font-size: ${({ theme }) => theme.typography.code.base.fontSize};
  }

  code {
    background-color: var(--color-background-secondary);
    padding: ${({ theme }) => theme.spacing[1]} ${({ theme }) => theme.spacing[2]};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
  }

  pre {
    background-color: var(--color-background-secondary);
    padding: ${({ theme }) => theme.spacing[4]};
    border-radius: ${({ theme }) => theme.borderRadius.md};
    overflow-x: auto;
    margin-bottom: ${({ theme }) => theme.spacing[4]};
  }

  /* Images */
  img {
    max-width: 100%;
    height: auto;
  }

  /* Tables */
  table {
    border-collapse: collapse;
    width: 100%;
    margin-bottom: ${({ theme }) => theme.spacing[4]};
  }

  th, td {
    padding: ${({ theme }) => theme.spacing[3]};
    text-align: left;
    border-bottom: 1px solid var(--color-border-primary);
  }

  th {
    font-weight: ${({ theme }) => theme.fontWeights.semiBold};
    background-color: var(--color-background-secondary);
  }

  /* Accessibility */
  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  /* Focus visible for better keyboard navigation */
  .focus-visible {
    outline: 2px solid var(--color-border-focus);
    outline-offset: 2px;
  }

  /* Reduced motion for accessibility */
  @media (prefers-reduced-motion: reduce) {
    *,
    *::before,
    *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }

  /* High contrast mode support */
  @media (prefers-contrast: high) {
    * {
      border-color: ButtonText !important;
    }
  }

  /* Print styles */
  @media print {
    * {
      background: transparent !important;
      color: black !important;
      box-shadow: none !important;
      text-shadow: none !important;
    }

    a, a:visited {
      text-decoration: underline;
    }

    a[href]:after {
      content: " (" attr(href) ")";
    }

    abbr[title]:after {
      content: " (" attr(title) ")";
    }

    pre, blockquote {
      border: 1px solid #999;
      page-break-inside: avoid;
    }

    thead {
      display: table-header-group;
    }

    tr, img {
      page-break-inside: avoid;
    }

    img {
      max-width: 100% !important;
    }

    p, h2, h3 {
      orphans: 3;
      widows: 3;
    }

    h2, h3 {
      page-break-after: avoid;
    }
  }
`;